"use client";

import { useState, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import { motion } from "framer-motion";
import { login } from "@/app/actions/auth";
import Link from "next/link";
import { ArrowRight, Camera, Lock, Mail, User } from "lucide-react";
import { deleteCookie } from "@/app/lib/utils/cookies";

export default function LoginPage() {
  const [error, setError] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();
  const returnUrl = searchParams.get('returnUrl');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;

    try {
      setIsLoading(true);
      setError(undefined);
      const result = await login(email, password, rememberMe);
      if (result.error) {
        setError(result.error);
      } else {
        router.refresh();
        
        // Clear the redirect flag and navigate to return URL if available
        if (returnUrl) {
          deleteCookie('isRedirecting');
          router.push(returnUrl);
        } else {
          router.push("/");
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-100 to-blue-100 flex justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div className="absolute inset-0 bg-grid-black/[0.02] bg-[size:20px_20px]" />
      </div>
      
      <div className="container flex gap-8 items-start max-w-6xl">
        {/* Left side - Login Form */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="w-full max-w-md space-y-8 bg-white/80 backdrop-blur-sm p-8 rounded-3xl shadow-xl border border-white/20"
        >
          <div className="text-center space-y-2">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-orange-500 bg-clip-text text-transparent">
              Hoş Geldiniz
            </h1>
            <p className="text-gray-600">Hesabınıza giriş yapın</p>
            {returnUrl && (
              <div className="mt-2 text-sm text-orange-600 bg-orange-50 p-2 rounded-md">
                Siparişinize devam etmek için lütfen giriş yapın
              </div>
            )}
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 text-sm text-red-800 bg-red-50 border border-red-100 rounded-xl"
            >
              {error}
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  E-posta
                </label>
                <div className="relative">
                  <Input
                    name="email"
                    type="email"
                    required
                    className="pl-10 bg-white/60"
                    placeholder="E-posta adresinizi girin"
                    disabled={isLoading}
                  />
                  <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Şifre
                </label>
                <div className="relative">
                  <Input
                    name="password"
                    type="password"
                    required
                    className="pl-10 bg-white/60"
                    placeholder="Şifrenizi girin"
                    disabled={isLoading}
                  />
                  <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span className="text-sm text-gray-600">Beni Hatırla</span>
              </label>

              <Link
                href="/sifremi-unuttum"
                className="text-sm text-gray-600 hover:text-rose-600 transition-colors"
              >
                Şifremi Unuttum
              </Link>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-orange-500 to-orange-500 hover:from-orange-500 hover:to-orange-500 text-white font-medium py-2.5"
            >
              {isLoading ? "Giriş yapılıyor..." : "Giriş Yap"}
            </Button>

            <div className="text-center text-sm text-gray-600">
              Hesabınız yok mu?{" "}
              <Link
                href={returnUrl ? `/kayit-ol?returnUrl=${encodeURIComponent(returnUrl)}` : "/kayit-ol"}
                className="font-medium text-orange-600 hover:text-orange-700 inline-flex items-center gap-1"
              >
                Kayıt Ol
                <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          </form>
        </motion.div>

        {/* Right side - Features */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex-1 hidden lg:block"
        >
          <div className="grid grid-cols-2 gap-6">
            <FeatureCard
              icon={<Camera className="h-6 w-6 text-orange-500" />}
              title="Profesyonel Albümler"
              description="Yüksek kaliteli fotoğraf albümleri ve özel tasarımlar"
            />
            <FeatureCard
              icon={<Mail className="h-6 w-6 text-orange-500" />}
              title="Sipariş Takip"
              description="SMS ve e-posta ile bilgilendirme"
            />
            <FeatureCard
              icon={<Lock className="h-6 w-6 text-orange-500" />}
              title="Güvenli Ödeme"
              description="SSL sertifikalı güvenli ödeme altyapısı"
            />
            <FeatureCard
              icon={<User className="h-6 w-6 text-orange-500" />}
              title="Özel Hesap"
              description="Size özel hesap yönetimi ve sipariş geçmişi"
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function FeatureCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl border border-white/20">
      <div className="bg-gray-50/50 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
        {icon}
      </div>
      <h3 className="font-medium text-gray-900 mb-1">{title}</h3>
      <p className="text-sm text-gray-600">{description}</p>
    </div>
  );
} 